import { useEffect, useState } from "react";
import api from "@/lib/api";
import { catMeta } from "@/lib/categories";
import { exportCallPdf } from "@/lib/pdf";
import {
  BarChart3, Phone, ShieldCheck, TrendingUp, Clock, Trophy, Zap, FileText,
  GraduationCap, Sparkles, AlertTriangle,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  LineChart, Line, CartesianGrid,
} from "recharts";

function fmtDur(s) {
  const m = Math.floor(s / 60); return `${m}m ${s % 60}s`;
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function History() {
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [coaching, setCoaching] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setSummary(r.data));
    api.get("/calls").then((r) => setCalls(r.data));
    api.get("/analytics/ranking").then((r) => setRanking(r.data));
    api.get("/analytics/coaching").then((r) => setCoaching(r.data)).catch(() => setCoaching({ points: [], per_user: [] }));
  }, []);

  const stats = [
    { label: "Chamadas", value: summary?.total_calls ?? 0, icon: Phone, color: "#2563EB" },
    { label: "Objeções encontradas", value: summary?.total_objections ?? 0, icon: BarChart3, color: "#F59E0B" },
    { label: "Objeções superadas", value: summary?.total_resolved ?? 0, icon: ShieldCheck, color: "#10B981" },
    { label: "Taxa de superação", value: `${summary?.resolution_rate ?? 0}%`, icon: TrendingUp, color: "#10B981" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-[#2563EB]" /> Histórico & Analytics
        </h1>
        <p className="text-[#94A3B8] text-sm mt-1">Inteligência de objeções: o que ouve mais e onde está a ganhar.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-2xl border border-white/10 bg-[#111827] p-5">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: s.color + "20" }}>
                <Icon className="w-5 h-5" style={{ color: s.color }} />
              </div>
              <div className="font-display text-3xl font-extrabold">{s.value}</div>
              <div className="text-xs text-[#94A3B8] mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
          <h3 className="font-display font-bold mb-4">Objeções por categoria</h3>
          {summary?.category_data?.some((d) => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary.category_data} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" stroke="#475569" fontSize={12} allowDecimals={false} />
                <YAxis type="category" dataKey="label" stroke="#94A3B8" fontSize={11} width={120} tickFormatter={(v) => v.split(" ")[0]} />
                <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {summary.category_data.map((d) => (
                    <Cell key={d.category} fill={catMeta(d.category).color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[#64748B] py-16 text-center">Ainda sem dados. Guarde a sua primeira chamada no Copiloto.</p>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
          <h3 className="font-display font-bold mb-4">Chamadas recentes</h3>
          <div className="space-y-2 max-h-[260px] overflow-y-auto">
            {calls.length === 0 ? (
              <p className="text-sm text-[#64748B] py-16 text-center">Nenhuma chamada guardada.</p>
            ) : calls.map((c) => (
              <div key={c.id} className="rounded-lg bg-[#0B0F17] border border-white/5 p-3" data-testid={`call-row-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm text-white">{c.lead_name}</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => exportCallPdf(c)}
                      data-testid={`export-pdf-${c.id}`}
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#2563EB]/20 text-[#93b4fb] hover:bg-[#2563EB]/30 flex items-center gap-1"
                      title="Descarregar relatório em PDF"
                    >
                      <FileText className="w-3 h-3" /> PDF
                    </button>
                    <div className="text-xs text-[#64748B]">{fmtDate(c.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-[#94A3B8]">
                  <span className="px-1.5 py-0.5 rounded bg-white/5">{c.platform}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDur(c.duration_seconds)}</span>
                  <span>{c.objections_count} objeções</span>
                  <span className="text-[#10B981]">{c.resolved_count} superadas</span>
                  <span className="text-[#64748B]">· {c.user_name}</span>
                </div>
                {c.summary && (
                  <div className="mt-2 flex items-start gap-1.5 text-xs text-[#94A3B8] border-t border-white/5 pt-2">
                    <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#2563EB]" />
                    <span className="leading-relaxed whitespace-pre-line">{c.summary}</span>
                  </div>
                )}
                {c.coaching && (c.coaching.strengths?.length || c.coaching.improvements?.length) && (
                  <div className="mt-2 rounded-md bg-[#111827] border border-white/5 p-2 text-xs" data-testid={`call-coaching-${c.id}`}>
                    <div className="flex items-center gap-1.5 mb-1.5 text-[#93b4fb] font-semibold">
                      <GraduationCap className="w-3.5 h-3.5" /> Auto-coaching
                      {c.coaching_score != null && (
                        <span className="ml-auto font-mono px-1.5 py-0.5 rounded bg-[#2563EB]/20 text-[#93b4fb]">{c.coaching_score}/100</span>
                      )}
                    </div>
                    {c.coaching.improvements?.length > 0 && (
                      <div className="mb-1">
                        <div className="text-[10px] uppercase tracking-wider text-[#F59E0B] font-bold flex items-center gap-1"><Sparkles className="w-3 h-3"/>Melhorias</div>
                        <ul className="list-disc pl-4 text-[#E2E8F0] leading-snug">{c.coaching.improvements.map((s,i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                    )}
                    {c.coaching.missed_opportunities?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#EF4444] font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>Oportunidades perdidas</div>
                        <ul className="list-disc pl-4 text-[#E2E8F0] leading-snug">{c.coaching.missed_opportunities.map((s,i) => <li key={i}>{s}</li>)}</ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#111827] p-5" data-testid="coaching-evolution-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-[#93b4fb]" /> Evolução do coaching da equipa
          </h3>
          {coaching?.team_average != null && (
            <div className="text-xs text-[#94A3B8]">
              Média da equipa: <span className="font-mono font-bold text-[#93b4fb]">{coaching.team_average}/100</span>
              {coaching.latest && <span className="ml-3">Última: <span className="font-mono">{coaching.latest.score}/100</span></span>}
            </div>
          )}
        </div>
        {coaching?.points?.length ? (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={coaching.points.map((p, i) => ({ ...p, i: i + 1, when: fmtDate(p.created_at) }))} margin={{ left: 4, right: 20, top: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="i" stroke="#475569" fontSize={11} tickLine={false} />
                <YAxis domain={[0, 100]} stroke="#475569" fontSize={11} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0B0F17", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  labelFormatter={(v, payload) => payload?.[0]?.payload?.when || `#${v}`}
                  formatter={(value, _name, ctx) => [`${value}/100`, ctx?.payload?.user_name || "Score"]}
                />
                <Line type="monotone" dataKey="score" stroke="#2563EB" strokeWidth={2} dot={{ fill: "#2563EB", r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
            {coaching.per_user?.length > 1 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="cue-label text-[#64748B] mb-2">Média por comercial</div>
                <div className="space-y-1.5">
                  {coaching.per_user.map((u) => (
                    <div key={u.user_id} className="flex items-center gap-3 text-sm" data-testid={`coaching-per-user-${u.user_id}`}>
                      <span className="flex-1 text-white truncate">{u.user_name}</span>
                      <span className="text-xs text-[#64748B]">{u.count} chamada(s)</span>
                      <span className="font-mono font-bold text-[#93b4fb] w-14 text-right">{u.average}/100</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-[#64748B] py-12 text-center">Ainda não há chamadas com auto-coaching guardado.</p>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
        <h3 className="font-display font-bold mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#F59E0B]" /> Ranking da Equipa
        </h3>
        <div className="space-y-2">
          {ranking?.ranking?.length ? ranking.ranking.map((r, i) => (
            <div key={r.user_id} className="flex items-center gap-3 rounded-lg bg-[#0B0F17] border border-white/5 p-3" data-testid={`ranking-row-${i}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-sm ${
                i === 0 ? "bg-[#F59E0B]/20 text-[#F59E0B]" : i === 1 ? "bg-white/10 text-white" : i === 2 ? "bg-[#F97316]/20 text-[#F97316]" : "bg-white/5 text-[#64748B]"
              }`}>{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-white truncate">{r.name}</div>
                <div className="text-xs text-[#64748B]">{r.calls} chamadas · {r.objections} objeções</div>
              </div>
              <div className="text-right">
                <div className="font-display font-extrabold text-lg text-[#10B981]">{r.resolved}</div>
                <div className="text-[10px] text-[#64748B]">superadas · {r.resolution_rate}%</div>
              </div>
            </div>
          )) : <p className="text-sm text-[#64748B] py-6 text-center">Ainda sem dados de ranking.</p>}
        </div>

        {ranking?.top_arguments?.length > 0 && (
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="cue-label text-[#64748B] mb-2">Argumentos mais eficazes</div>
            <div className="space-y-2">
              {ranking.top_arguments.slice(0, 5).map((a) => {
                const m = catMeta(a.category);
                return (
                  <div key={a.id} className="flex items-start gap-2 rounded-lg bg-[#0B0F17] border border-white/5 p-3">
                    <Zap className="w-4 h-4 shrink-0 mt-0.5" fill={m.color} color={m.color} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#F8FAFC]">{a.argument || a.trigger}</p>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded mt-1 inline-block" style={{ color: m.color, backgroundColor: m.bg }}>
                        {m.short} · eficácia {a.effectiveness}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
